import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, errMsg, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/hooks/useTheme";
import { useCompany } from "@/hooks/useCompany";
import { useMenu } from "@/hooks/useMenu";
import { areaOfPath } from "@/lib/menu";
import { toast } from "sonner";
import { useIdleLogout } from "@/hooks/useIdleLogout";
import { Buildings, CaretDown, Moon, Sun, SignOut, ArrowLeft, House } from "@phosphor-icons/react";

const ROLE_LABEL = { admin: "Amministratore", titolare: "Titolare", manager: "Responsabile", employee: "Dipendente", rspp: "RSPP" };

export const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const company = useCompany();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { areas } = useMenu(user);
  const [companies, setCompanies] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!["admin", "manager"].includes(user?.role)) return;
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  }, [user?.role]);

  const switchCompany = async (id) => {
    try {
      const r = await api.post(`/companies/${id}/select`);
      toast.success(`Azienda attiva: ${r.data.name}`);
      setPickerOpen(false);
      window.location.reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const idleLogout = useCallback(async () => {
    await logout();
    toast.info("Sessione terminata per inattività");
    navigate("/login");
  }, [logout, navigate]);
  // nessun logout automatico per Amministratore, Titolare e Responsabile
  const minutiInattivita = ["admin", "titolare", "manager"].includes(user?.role) ? 0 : 5;
  useIdleLogout(idleLogout, minutiInattivita);

  const areaCorrente = useMemo(() => {
    if (pathname === "/") {
      const k = new URLSearchParams(search).get("area");
      const a = areas.find((x) => x.key === k);
      return a ? { area: a, item: null } : null;
    }
    const trovato = areaOfPath(user, pathname);
    if (!trovato) return null;
    const a = areas.find((x) => x.key === trovato.area.key) || trovato.area;
    return { area: a, item: trovato.item };
  }, [areas, pathname, search, user]);

  const esci = () => {
    if (pathname !== "/") {
      navigate(areaCorrente ? `/?area=${areaCorrente.area.key}` : "/");
      return;
    }
    navigate("/");
  };

  const briciole = ["Menu Principale"];
  if (areaCorrente?.area) briciole.push(areaCorrente.area.title);
  if (areaCorrente?.item) briciole.push(areaCorrente.item.label);

  const inMenuPrincipale = pathname === "/" && !areaCorrente;

  // ESC e F3 tornano al livello precedente anche dalle pagine operative
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape" && e.key !== "F3" && e.key !== "F8") return;
      const t = e.target;
      if (t && (["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName) || t.isContentEditable)) return;
      if (document.querySelector('[role="dialog"]')) return;
      if (pathname === "/") return;
      e.preventDefault();
      esci();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="h-14 border-b border-border bg-card flex items-center gap-3 px-3 sm:px-4 sticky top-0 z-20">
        <button onClick={() => navigate("/")} data-testid="brand-home"
          className="flex items-center gap-2 min-w-0 hover:opacity-80 transition-opacity duration-150">
          {company.id && company.logo_path ? (
            <img src={`${API}/companies/${company.id}/logo`} alt="" className="h-7 w-7 shrink-0 rounded-sm object-contain bg-white" />
          ) : (
            <div className="h-7 w-7 shrink-0 rounded-sm bg-primary flex items-center justify-center text-primary-foreground font-head font-extrabold text-sm">
              {(company.name || "B").charAt(0).toUpperCase()}
            </div>
          )}
          <span className="leading-tight text-left min-w-0 hidden sm:block">
            <span className="block font-head font-bold text-sm truncate" data-testid="header-company">{company.name}</span>
            <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">Gestionale aziendale</span>
          </span>
        </button>
        <div className="text-sm text-muted-foreground tabular-nums hidden lg:block ml-2">
          {new Date().toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {!inMenuPrincipale && (
            <button onClick={esci} data-testid="exit-btn" title="Indietro (ESC / F8)"
              className="h-10 sm:h-8 px-3 flex items-center gap-1.5 rounded-sm border border-border text-sm font-semibold hover:bg-secondary transition-colors duration-150">
              <ArrowLeft size={15} /> INDIETRO
            </button>
          )}
          {!inMenuPrincipale && (
            <button onClick={() => navigate("/")} data-testid="main-menu-btn" title="Menu principale"
              className="h-10 w-10 sm:h-8 sm:w-8 flex items-center justify-center rounded-sm border border-border hover:bg-secondary transition-colors duration-150">
              <House size={16} />
            </button>
          )}
          {["admin", "manager"].includes(user?.role) && (
            <div className="relative">
              <button data-testid="company-switcher" onClick={() => setPickerOpen((v) => !v)}
                className="h-8 max-w-[200px] px-2.5 hidden sm:flex items-center gap-1.5 rounded-sm border border-border text-sm font-medium hover:bg-secondary transition-colors duration-150">
                <Buildings size={15} className="shrink-0" />
                <span className="truncate">{company.name}</span>
                <CaretDown size={12} className="shrink-0" />
              </button>
              {pickerOpen && (
                <div className="absolute right-0 mt-1 w-64 rounded-sm border border-border bg-card shadow-lg z-30 p-1"
                  data-testid="company-switcher-menu">
                  {companies.map((c) => (
                    <button key={c.id} onClick={() => switchCompany(c.id)} data-testid={`company-switch-${c.id}`}
                      className={`w-full text-left px-2.5 py-2 rounded-sm text-sm hover:bg-secondary truncate ${
                        c.id === company.id ? "font-semibold text-primary" : ""}`}>
                      {c.name}
                    </button>
                  ))}
                  <button onClick={() => { setPickerOpen(false); navigate("/aziende"); }} data-testid="company-manage-link"
                    className="block w-full text-left px-2.5 py-2 mt-1 border-t border-border text-xs text-muted-foreground hover:text-foreground">
                    Gestisci aziende
                  </button>
                </div>
              )}
            </div>
          )}
          <span data-testid="header-user-role" className="hidden md:inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-secondary text-secondary-foreground">
            {ROLE_LABEL[user?.role] || user?.role}
          </span>
          <button data-testid="theme-toggle" onClick={toggle} title="Tema chiaro/scuro"
            className="h-10 w-10 sm:h-8 sm:w-8 flex items-center justify-center rounded-sm border border-border hover:bg-secondary transition-colors duration-150">
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button data-testid="logout-btn" title="Logout" onClick={async () => { await logout(); navigate("/login"); }}
            className="h-10 sm:h-8 px-3 flex items-center gap-1.5 rounded-sm border border-border text-sm font-medium hover:bg-secondary transition-colors duration-150">
            <SignOut size={15} /> <span className="hidden sm:inline">LOGOUT</span>
          </button>
        </div>
      </header>

      {!inMenuPrincipale && (
        <div className="border-b border-border bg-card/60 px-3 sm:px-4 py-1.5 font-mono text-[11px] text-muted-foreground flex items-center gap-1.5 overflow-x-auto"
          data-testid="breadcrumb">
          {briciole.map((b, i) => (
            <span key={b} className="flex items-center gap-1.5 whitespace-nowrap">
              {i > 0 && <span className="opacity-60">&gt;</span>}
              <button type="button" data-testid={`breadcrumb-${i}`}
                onClick={() => (i === 0 ? navigate("/") : i === 1 && areaCorrente?.area ? navigate(`/?area=${areaCorrente.area.key}`) : null)}
                className={i === briciole.length - 1 ? "font-semibold text-foreground" : "hover:text-foreground"}>
                {b.toUpperCase()}
              </button>
            </span>
          ))}
          <span className="ml-auto hidden sm:block">ESC / F8 = indietro</span>
        </div>
      )}

      <main className="flex-1 p-3 sm:p-4 min-w-0">{children}</main>
    </div>
  );
};

export default AppLayout;
